// components/sections/PaymentHistorySummarySection.tsx

interface Props {
  data: {
    receivableNumber: string;
    customerName: string;
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    status?: string;
  };
}

const PaymentHistorySummarySection = ({ data }: Props) => {
  const {
    receivableNumber,
    customerName,
    totalAmount,
    paidAmount,
    pendingAmount,
    status,
  } = data;

  const statusStyles: Record<string, string> = {
    PAID: 'border-meta-3/20 bg-meta-3/10 text-meta-3',
    PARTIAL: 'border-warning/20 bg-warning/10 text-warning',
    PENDING: 'border-meta-6/20 bg-meta-6/10 text-meta-6',
    OVERDUE: 'border-danger/20 bg-danger/10 text-danger',
  };

  const statusLabel = status || 'PENDING';

  return (
    <section
      className="
    p-6
    dark:border-strokedark
  "
    >
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h4 className="text-base font-semibold text-black dark:text-white">
            Historial de Pagos
          </h4>

          <p className="text-sm text-body dark:text-bodydark">
            Resumen financiero actual de la cuenta por cobrar
          </p>
        </div>

        <span
          className={`
            inline-flex
            rounded-full
            border
            px-4
            py-2
            text-sm
            font-semibold
            ${
              statusStyles[statusLabel] ||
              'border-meta-6/20 bg-meta-6/10 text-meta-6'
            }
          `}
        >
          {statusLabel}
        </span>
      </div>

      {/* Grid principal */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {/* Cuenta */}
        <div
          className="
            rounded-xl
            border border-stroke
            bg-gray-2
            p-4
            dark:border-strokedark
            dark:bg-boxdark-2
          "
        >
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-body dark:text-bodydark">
            Cuenta
          </p>

          <p className="text-base font-semibold text-black dark:text-white">
            {receivableNumber || '-'}
          </p>
        </div>

        {/* Cliente */}
        <div
          className="
            rounded-xl
            border border-stroke
            bg-gray-2
            p-4
            dark:border-strokedark
            dark:bg-boxdark-2
          "
        >
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-body dark:text-bodydark">
            Cliente
          </p>

          <p className="text-base font-semibold text-black dark:text-white">
            {customerName || '-'}
          </p>
        </div>

        {/* Total */}
        <div
          className="
            rounded-xl
            border border-primary/20
            bg-primary/5
            p-4
          "
        >
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-body">
            Total Original
          </p>

          <p className="text-title-xsm font-bold text-primary">
            ${Number(totalAmount || 0).toFixed(2)}
          </p>
        </div>

        {/* Pagado */}
        <div
          className="
            rounded-xl
            border border-meta-3/20
            bg-meta-3/5
            p-4
          "
        >
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-body">
            Total Pagado
          </p>

          <p className="text-title-xsm font-bold text-meta-3">
            ${Number(paidAmount || 0).toFixed(2)}
          </p>
        </div>

        {/* Pendiente */}
        <div
          className="
            rounded-xl
            border border-danger/20
            bg-danger/5
            p-4
          "
        >
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-body">
            Saldo Pendiente
          </p>

          <p className="text-title-xsm font-bold text-danger">
            ${Number(pendingAmount || 0).toFixed(2)}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PaymentHistorySummarySection;
